import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { color, r, radius } from '../../theme/tokens';
import useMedicationSearch, { resolveMedicationPick } from '../../hooks/useMedicationSearch';
import { addCheckerMedication } from '../../utils/interactionCheckerStore';
import { logAnalyticsEvent } from '../../utils/analytics';
import PickField, { DropdownMessage, Highlight, OptionRow, listboxIdFor } from './PickField';
import {
  ADDING,
  COULDNT_ADD,
  GENERIC,
  MEDICATIONS_HEADING,
  MEDICATIONS_INSTRUCTION,
  MEDICATIONS_PLACEHOLDER,
  MEDICATION_NO_MATCH,
  MEDICATION_SEARCH_FAILED,
  SEARCHING,
  TRY_AGAIN,
  brandOf,
  noMatchTitle,
} from './copy';

// v5.1's medications field (STACK-587, states 1–3 and the no-match edge). The
// search is the composer panel's (`useMedicationSearch`): the POST lookups,
// 2 characters, a 300 ms pause. Only a result RxNorm resolves to an
// ingredient can be added; there is no free text.

const FIELD_ID = 'checker-medications';

const sameName = (a, b) => Boolean(a && b) && a.trim().toLowerCase() === b.trim().toLowerCase();

const isAlreadyAdded = (result, added) => added.some((m) =>
  sameName(result.display_name, m.display_name)
  || sameName(result.generic_name, m.ingredient_name)
  || sameName(result.display_name, m.ingredient_name));

// v5.1: "Generic", or "Brand of warfarin".
const resultSubline = (result) => (result.is_brand && result.generic_name ? brandOf(result.generic_name) : GENERIC);

export default function MedicationField({ medications, atCap, signedIn, onRemove }) {
  const [term, setTerm] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [adding, setAdding] = useState(null);
  const [addError, setAddError] = useState(null);
  const { query, results, view, retry } = useMedicationSearch(term);
  // What is typed now, so a slow add does not wipe what was typed meanwhile.
  const termRef = useRef(term);
  termRef.current = term;
  const matches = results.filter((res) => !isAlreadyAdded(res, medications));

  useEffect(() => { setHighlight(0); }, [results]);

  const pick = async (result) => {
    if (!result || adding) return;
    setAddError(null);
    setAdding(result.display_name);
    const typed = termRef.current;
    const clearIfUnchanged = () => { if (termRef.current === typed) setTerm(''); };
    try {
      const resolved = await resolveMedicationPick(result);
      const outcome = resolved ? addCheckerMedication(resolved) : 'invalid';
      if (outcome === 'added') {
        clearIfUnchanged();
        logAnalyticsEvent('checker_medication_added', {
          in_rxcui: resolved.in_rxcui,
          ingredient_name: resolved.ingredient_name,
          count: medications.length + 1,
        });
      } else if (outcome !== 'duplicate') {
        setAddError(result.display_name);
      } else {
        clearIfUnchanged();
      }
    } catch (_) {
      setAddError(result.display_name);
    } finally {
      setAdding(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setTerm('');
      return;
    }
    if (view !== 'results' || !matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(matches[highlight]);
    }
  };

  const optionId = (i) => `${FIELD_ID}-option-${i}`;

  const dropdown = () => {
    if (addError) {
      return <DropdownMessage role="alert">{COULDNT_ADD(addError)}</DropdownMessage>;
    }
    if (adding) return <DropdownMessage>{ADDING}</DropdownMessage>;
    if (view === 'loading') {
      return <DropdownMessage>{SEARCHING}</DropdownMessage>;
    }
    if (view === 'error') {
      return (
        <DropdownMessage role="alert">
          {MEDICATION_SEARCH_FAILED}
          <Box>
            <ButtonBase
              onClick={retry}
              sx={{
                mt: r(10),
                minHeight: r(44),
                px: r(16),
                borderRadius: radius.md,
                border: `${r(1)} solid ${color.line}`,
                backgroundColor: color.paper,
                color: color.evergreen,
                fontWeight: 600,
                fontSize: r(14),
              }}
            >
              {TRY_AGAIN}
            </ButtonBase>
          </Box>
        </DropdownMessage>
      );
    }
    if (view === 'none' || (view === 'results' && !matches.length)) {
      return <DropdownMessage title={noMatchTitle(query)}>{MEDICATION_NO_MATCH}</DropdownMessage>;
    }
    if (view === 'results') {
      return (
        <Box component="ul" role="listbox" id={listboxIdFor(FIELD_ID)} aria-label="Matching medications" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {matches.map((res, i) => (
            <OptionRow
              key={`${res.display_name}-${i}`}
              id={optionId(i)}
              selected={i === highlight}
              onSelect={() => pick(res)}
              onHover={() => setHighlight(i)}
            >
              <Typography component="span" sx={{ fontSize: r(15), color: color.ink, lineHeight: 1.35 }}>
                <Highlight text={res.display_name} term={query} />
              </Typography>
              <Typography component="span" sx={{ fontSize: r(13), color: color.inkSoft, mt: r(2) }}>
                {resultSubline(res)}
              </Typography>
            </OptionRow>
          ))}
        </Box>
      );
    }
    return null;
  };

  const content = dropdown();

  return (
    <PickField
      id={FIELD_ID}
      heading={MEDICATIONS_HEADING}
      instruction={MEDICATIONS_INSTRUCTION}
      placeholder={MEDICATIONS_PLACEHOLDER}
      chips={medications.map((m) => ({ id: m.in_rxcui, label: m.display_name }))}
      onRemove={onRemove}
      atCap={atCap}
      signedIn={signedIn}
      term={term}
      onTermChange={(value) => { setTerm(value); setAddError(null); }}
      onKeyDown={onKeyDown}
      dropdownOpen={Boolean(content)}
      dropdown={content}
      activeOptionId={view === 'results' && matches.length ? optionId(highlight) : undefined}
    />
  );
}
